import type { ColumnDef, EnemyDef, LevelDef, PickupDef, SegmentDef } from '../../types';

// OP-302 Tequila Express (GDD §10.5, §10.2, issue #120): a Guadalajara afternoon, written by hand with the format
// of §15.6. 600 u at 6.25 u/s (world 3, level 2): takeoff, warm-up (20 %), tension (30 %) up to CP-302-A at x 300,
// respite (10 %), peak (40 %) and the clandestine airstrip. Gap columns every 8 u with the 3.5 u gap of world 3
// (§5.4), the gap centre never moving more than 3.5 u, all with the `w3-agave` skin (#115). The tequila train
// (EN-33, #118) and Don Nieto's sombreros (EN-32, #117) are taught one at a time: the first train comes before the
// first balcony, and each is left alone. The world 3 escapular hides in a risky route, within the reach of a
// sombrero. The peak adds a crop duster (EN-31) and federales (EN-02). An armor before the checkpoint and a bribe
// in the respite. All x are local.

const column = (x: number, gapY: number): ColumnDef => ({ x, gapY, gapH: 3.5, skin: 'w3-agave' });
const dollar = (x: number, y: number): PickupDef => ({ x, y, type: 'dollar' });
const armor = (x: number, y: number): PickupDef => ({ x, y, type: 'armor' });
const bribe = (x: number, y: number): PickupDef => ({ x, y, type: 'bribe' });
const scapular = (x: number, y: number): PickupDef => ({ x, y, type: 'scapular' });
/** The tequila train (EN-33): a ground-level body whose smoke blocks the view. */
const train = (x: number): EnemyDef => ({ x, y: 1, type: 'EN-33' });
/** Don Nieto's balcony (EN-32), mounted on a column piece; it throws sombreros that come back like a boomerang. */
const balcony = (x: number, y: number): EnemyDef => ({ x, y, type: 'EN-32' });
/** A crop duster (EN-31), on the peak only. */
const duster = (x: number, y: number): EnemyDef => ({ x, y, type: 'EN-31' });
/** A helicopter of the federales (EN-02), hovering high. */
const federales = (x: number, y: number): EnemyDef => ({ x, y, type: 'EN-02' });

const takeoff: SegmentDef = { id: 'w3-tequila-takeoff-01', kind: 'takeoff', length: 40, columns: [], enemies: [], pickups: [] };

const warmup01: SegmentDef = {
  id: 'w3-tequila-warmup-01',
  kind: 'pattern',
  phase: 'warmup',
  length: 50,
  columns: [
    column(20.0, 6.0),
    column(28.0, 6.5),
    column(36.0, 6.0),
    column(44.0, 5.5),
  ],
  enemies: [],
  pickups: [
    dollar(23.25, 6.0),
    dollar(24.25, 6.3),
    dollar(25.25, 6.0),
    dollar(39.25, 5.5),
    dollar(40.25, 5.8),
    dollar(41.25, 5.5),
  ],
};

const warmup02: SegmentDef = {
  id: 'w3-tequila-warmup-02',
  kind: 'pattern',
  phase: 'warmup',
  length: 50,
  columns: [
    column(3.0, 6.0),
    column(11.0, 6.5),
    column(19.0, 6.0),
    column(27.0, 6.5),
    column(35.0, 6.0),
    column(43.0, 5.5),
  ],
  enemies: [],
  pickups: [
    dollar(15.25, 6.0),
    dollar(16.25, 6.3),
    dollar(17.25, 6.0),
    dollar(31.25, 6.5),
    dollar(32.25, 6.8),
    dollar(33.25, 6.5),
  ],
};

const tension01: SegmentDef = {
  id: 'w3-tequila-tension-01',
  kind: 'pattern',
  phase: 'tension',
  length: 50,
  // The first tequila train comes alone (GDD §10.5); the chimney is far from the next column at the crossing.
  columns: [column(20.0, 6.0), column(30.0, 6.5), column(46.0, 6.0)],
  enemies: [train(38.0)],
  pickups: [
    dollar(23.25, 6.0),
    dollar(24.25, 6.3),
    dollar(25.25, 6.0),
    dollar(41.25, 6.25),
    dollar(42.25, 6.55),
    dollar(43.25, 6.25),
  ],
};

const tension02: SegmentDef = {
  id: 'w3-tequila-tension-02',
  kind: 'pattern',
  phase: 'tension',
  length: 50,
  // The first balcony (Don Nieto), mounted on the upper piece of the column at x 20 (gap 6.0, so the upper piece
  // starts at 7.75; the balcony at y 9.0 sits inside it).
  columns: [column(20.0, 6.0), column(28.0, 6.5), column(36.0, 6.0), column(44.0, 6.5)],
  enemies: [balcony(20.0, 9.0)],
  pickups: [
    dollar(31.25, 6.25),
    dollar(32.25, 6.55),
    dollar(33.25, 6.25),
    dollar(47.25, 6.25),
    dollar(48.25, 6.55),
    dollar(49.25, 6.25),
    armor(13.25, 6.25),
  ],
};

const tension03: SegmentDef = {
  id: 'w3-tequila-tension-03',
  kind: 'pattern',
  phase: 'tension',
  length: 50,
  // A clear stretch for the train: its chimney (front) stays 6 u or more from the next column.
  columns: [column(6.0, 6.0), column(14.0, 6.5), column(22.0, 6.0), column(38.0, 6.5), column(46.0, 6.0)],
  enemies: [train(32.0)],
  pickups: [
    dollar(17.25, 6.5),
    dollar(18.25, 6.8),
    dollar(19.25, 6.5),
    dollar(43.25, 6.5),
    dollar(44.25, 6.8),
    dollar(45.25, 6.5),
  ],
};

const respite01: SegmentDef = {
  id: 'w3-tequila-respite-01',
  kind: 'pattern',
  phase: 'respite',
  length: 50,
  columns: [column(5.0, 6.0), column(13.0, 6.5), column(21.0, 6.0), column(29.0, 6.5), column(37.0, 6.0), column(45.0, 6.5)],
  enemies: [],
  pickups: [
    dollar(17.25, 6.0),
    dollar(18.25, 6.3),
    dollar(19.25, 6.0),
    dollar(33.25, 6.5),
    dollar(34.25, 6.8),
    dollar(35.25, 6.5),
    bribe(9.25, 6.25),
  ],
};

const peak01: SegmentDef = {
  id: 'w3-tequila-peak-01',
  kind: 'pattern',
  phase: 'peak',
  length: 67,
  columns: [
    column(6.0, 6.0),
    column(14.0, 6.5),
    column(22.0, 6.0),
    column(30.0, 5.5),
    column(38.0, 6.0),
    column(54.0, 6.0),
    column(62.0, 6.5),
  ],
  // A balcony on the column at x 30 (gap 5.5, upper piece from 7.25), a train in the clear stretch (x 46), far apart.
  enemies: [balcony(30.0, 8.5), train(46.0)],
  pickups: [
    dollar(17.25, 6.25),
    dollar(18.25, 6.55),
    dollar(19.25, 6.25),
    dollar(43.25, 6.25),
    dollar(44.25, 6.55),
    dollar(45.25, 6.25),
    // The world 3 escapular hides within the reach of the balcony at x 30 (6 u to its left, y 8.5): a risk route.
    scapular(24.5, 8.5),
  ],
};

const peak02: SegmentDef = {
  id: 'w3-tequila-peak-02',
  kind: 'pattern',
  phase: 'peak',
  length: 67,
  columns: [
    column(5.0, 6.0),
    column(13.0, 6.0),
    column(21.0, 6.5),
    column(29.0, 6.0),
    column(37.0, 6.0),
    column(45.0, 6.5),
    column(53.0, 6.0),
    column(61.0, 6.0),
  ],
  enemies: [train(31.0), federales(56.0, 11.0)],
  pickups: [
    dollar(16.25, 6.25),
    dollar(17.25, 6.55),
    dollar(18.25, 6.25),
    dollar(42.25, 6.25),
    dollar(43.25, 6.55),
    dollar(44.25, 6.25),
  ],
};

const peak03: SegmentDef = {
  id: 'w3-tequila-peak-03',
  kind: 'pattern',
  phase: 'peak',
  length: 66,
  columns: [
    column(4.0, 6.0),
    column(12.0, 6.5),
    column(20.0, 6.0),
    column(28.0, 6.0),
    column(36.0, 6.5),
    column(44.0, 6.0),
    column(52.0, 6.0),
    column(60.0, 6.5),
  ],
  enemies: [balcony(44.0, 8.5), duster(24.0, 8.0)],
  pickups: [
    dollar(15.25, 6.25),
    dollar(16.25, 6.55),
    dollar(17.25, 6.25),
    dollar(39.25, 6.25),
    dollar(40.25, 6.55),
    dollar(41.25, 6.25),
  ],
};

const airstrip: SegmentDef = { id: 'w3-tequila-airstrip-01', kind: 'airstrip', length: 60, columns: [], enemies: [], pickups: [] };

export const OP_302_SEGMENTS: readonly SegmentDef[] = [takeoff, warmup01, warmup02, tension01, tension02, tension03, respite01, peak01, peak02, peak03, airstrip];

export const OP_302: LevelDef = {
  id: '3-2',
  op: 'OP-302',
  name: 'Tequila Express',
  world: 3,
  scrollSpeed: 6.25,
  timeOfDay: 'afternoon',
  segments: OP_302_SEGMENTS.map((segment) => segment.id),
  checkpoints: [{ id: 'CP-302-A', x: 300 }],
  ending: 'airstrip',
};
